import { Item } from "@aitisal/contracts/restaurant/menu";
import { useEffect, useState } from "react";
import { getTitle } from "../helper/helper";
import { useMenuAggregate } from "./useMenuAggregate";

/**
 * Shared listing state for the product listing page: sort, filter,
 * pagination and the derived item lists. Theme page slots are
 * presentation-only over this hook.
 */

export const PRICE_RANGES = [
  "Under Rs. 30,000",
  "Rs. 30,000 - Rs. 50,000",
  "Rs. 50,000 - Rs. 70,000",
  "Rs. 70,000 - Rs. 100,000",
  "Above Rs. 100,000",
] as const;

export const PRICE_RANGE_BOUNDS: Record<
  (typeof PRICE_RANGES)[number],
  [number, number]
> = {
  "Under Rs. 30,000": [0, 30000],
  "Rs. 30,000 - Rs. 50,000": [30000, 50000],
  "Rs. 50,000 - Rs. 70,000": [50000, 70000],
  "Rs. 70,000 - Rs. 100,000": [70000, 100000],
  "Above Rs. 100,000": [100000, Infinity],
};

export interface ListingFilters {
  categories: string[];
  priceRanges: string[];
  colors: string[];
  sizes: string[];
}

const EMPTY_FILTERS: ListingFilters = {
  categories: [],
  priceRanges: [],
  colors: [],
  sizes: [],
};

export interface UseProductListingArgs {
  preselectedCategory?: string;
  searchQuery?: string;
}

export function useProductListing({
  preselectedCategory,
  searchQuery,
}: UseProductListingArgs) {  const [sortBy, setSortBy] = useState("featured");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;
  const [selectedFilters, setSelectedFilters] =
    useState<ListingFilters>(EMPTY_FILTERS);

  const { data: menusData, isLoading, error } = useMenuAggregate();

  // Reset page to 1 whenever filters or sort change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedFilters, sortBy]);

  // Handle pre-selected category from URL
  useEffect(() => {
    if (preselectedCategory) {
      setSelectedFilters((prev) => ({
        ...prev,
        categories: [preselectedCategory],
      }));
    }
  }, [preselectedCategory]);

  // --- SAFE FILTER HANDLER (Prevents "not iterable" error) ---
  const handleFilterChange = (
    type: string,
    value: string,
    checked: boolean,
  ) => {
    if (type === "all") {
      setSelectedFilters(EMPTY_FILTERS);
      return;
    }

    // Explicitly map singular 'type' from checkbox to plural state key
    let stateKey: keyof ListingFilters;
    if (type === "category") stateKey = "categories";
    else if (type === "priceRange") stateKey = "priceRanges";
    else if (type === "color") stateKey = "colors";
    else if (type === "size") stateKey = "sizes";
    else return;

    setSelectedFilters((prev) => {
      const currentArray = prev[stateKey];
      const newArray = checked
        ? [...currentArray, value]
        : currentArray.filter((item) => item !== value);

      return { ...prev, [stateKey]: newArray };
    });
  };

  const clearFilters = () => {
    setSelectedFilters(EMPTY_FILTERS);
  };

  const allItems = Array.from(
    new Map(
      (menusData?.menus?.flatMap((m: any) => m.sections?.flatMap((s: any) => s.items || []) || []) || [])
        .map((item: Item) => [item.itemId, item]),
    ).values(),
  ) as Item[];
  const allSections = menusData?.menus?.[0]?.sections || [];

  // --- Compute Allowed IDs based on Selected Categories ---
  const allowedItemIds = Array.from(
    new Set(
      selectedFilters.categories.length === 0
        ? [] // If no category selected, this list is empty (logic below handles "all" case)
        : allSections
            ?.filter((section: any) =>
              selectedFilters.categories.includes(getTitle(section.name)),
            ) // Find matching sections
            ?.flatMap(
              (section: any) => section.items?.map((i: any) => i.itemId) || [],
            ) || [], // Extract IDs
    ),
  );

  const filteredItems = allItems.filter((item) => {
    // 1. SEARCH FILTER (by item name)
    if (searchQuery && searchQuery.trim()) {
      const searchLower = searchQuery.toLowerCase();
      const itemName = getTitle(item.name).toLowerCase();
      if (!itemName.includes(searchLower)) {
        return false;
      }
    }

    // 2. CATEGORY FILTER (Using the ID list we computed above)
    if (selectedFilters.categories.length > 0) {
      if (!allowedItemIds.includes(item.itemId)) {
        return false;
      }
    }

    // 3. PRICE FILTER
    if (selectedFilters.priceRanges.length > 0) {
      const price = item.price / 100;
      const inPriceRange = selectedFilters.priceRanges.some((range: string) => {
        const bounds = PRICE_RANGE_BOUNDS[range as keyof typeof PRICE_RANGE_BOUNDS];
        if (!bounds) return true;
        return price >= bounds[0] && price <= bounds[1];
      });
      if (!inPriceRange) return false;
    }

    return true;
  });

  const sortedItems = [...filteredItems].sort((a, b) => {
    switch (sortBy) {
      case "price-low":
        return a.price - b.price;
      case "price-high":
        return b.price - a.price;
      case "name":
        return getTitle(a.name).localeCompare(getTitle(b.name));
      default:
        return 0;
    }
  });

  // Extract available categories from sections
  const categories = Array.from(
    new Set(
      (allSections || []).map((section: any) => getTitle(section.name)),
    ),
  );

  // --- PAGINATION ---
  const validItems = sortedItems.filter(
    (item: Item) => item.isSeparatelySellable,
  );
  const totalPages = Math.ceil(validItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentDisplayItems = validItems.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return {
    isLoading,
    error,
    sortBy,
    setSortBy,
    currentPage,
    itemsPerPage,
    selectedFilters,
    handleFilterChange,
    clearFilters,
    categories,
    validItems,
    currentDisplayItems,
    totalPages,
    startIndex,
    handlePageChange,
  };
}

export type UseProductListingReturn = ReturnType<typeof useProductListing>;
